import {
  Box,
  Button,
  Divider,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import IOSSwitch from "../../common/form/IOSSwitch";

interface MultipleChoiceEditorSidebarProps {
  hasImage: boolean;
  setHasImage: (value: boolean) => void;
  hasAdditionalContext: boolean;
  setHasAdditionalContext: (value: boolean) => void;
  onAddQuestionOption: () => void;
  hint: string;
  setHint: (value: string) => void;
  isMultiSelect: boolean;
  isAddOptionDisabled: boolean;
}

export default function MultipleChoiceEditorSidebar({
  hasImage,
  setHasImage,
  hasAdditionalContext,
  setHasAdditionalContext,
  onAddQuestionOption,
  hint,
  setHint,
  isMultiSelect,
  isAddOptionDisabled,
}: MultipleChoiceEditorSidebarProps) {
  const boxHeight = "calc(100vh - 68px)";
  const theme = useTheme();
  return (
    <Box
      maxHeight={boxHeight}
      padding="24px"
      sx={{
        backgroundColor: theme.palette.Neutral[100],
        minWidth: "300px",
        maxWidth: "fit-content",
        overflowY: "auto",
        gapY: "24px",
        paddingBottom: "24px",
        flexDirection: "column",
        alignItems: "flex-start",
        flex: "1 0 0",
        alignSelf: "stretch",
      }}
      className="no-scrollbar"
    >
      <Box
        sx={{
          display: "flex",
          padding: "12px 12px 16px 24px",
          alignItems: "flex-start",
          alignSelf: "stretch",
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "48px",
            paddingTop: "12px",
            alignItems: "flex-start",
            flex: "1 0 0",
          }}
        >
          <Typography variant="titleLarge" sx={{ flex: "1 0 0" }}>
            {isMultiSelect ? "Multi Select" : "Select One"}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          padding: "24px",
          flexDirection: "column",
          alignItems: "center",
          flex: "1 0 0",
          alignSelf: "stretch",
        }}
      >
        <Box
          sx={{
            display: "flex",
            paddingBottom: "24px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "24px",
            flex: "1 0 0",
            alignSelf: "stretch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "8px",
                alignSelf: "stretch",
              }}
            >
              <Typography variant="titleSmall">Include</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignSelf: "stretch",
              }}
            >
              <Typography variant="bodySmall">Image</Typography>
              <IOSSwitch
                checked={hasImage}
                onChange={(ev) => setHasImage(ev.target.checked)}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignSelf: "stretch",
              }}
            >
              <Typography variant="bodySmall">Additional context</Typography>
              <IOSSwitch
                checked={hasAdditionalContext}
                onChange={(ev) => setHasAdditionalContext(ev.target.checked)}
              />
            </Box>
          </Box>
          <Divider
            sx={{
              background: theme.palette.Neutral[400],
              width: "252px",
              height: "1px",
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "16px",
              alignSelf: "stretch",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "8px",
                alignSelf: "stretch",
              }}
            >
              <Typography variant="titleSmall">Question options</Typography>
              <Typography variant="bodySmall">
                Add options associated to the question
              </Typography>
            </Box>
            <Tooltip
              title={
                isAddOptionDisabled
                  ? "Maximum number of options (4) reached"
                  : undefined
              }
            >
              <Button
                sx={{
                  display: "flex",
                  padding: "10px 24px 10px 16px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                  flex: "1 0 0",
                  alignSelf: "stretch",

                  borderRadius: "4px",
                  border: `1px solid ${theme.palette.Neutral[500]}`,

                  opacity: isAddOptionDisabled ? 0.55 : undefined,

                  "&:hover": {
                    backgroundColor: isAddOptionDisabled
                      ? undefined
                      : theme.palette.Administrator.Light.Hover,
                  },
                }}
                onClick={() => {
                  if (isAddOptionDisabled) return;
                  onAddQuestionOption();
                }}
              >
                <Typography
                  variant="labelLarge"
                  sx={{ color: theme.palette.Administrator.Dark.Default }}
                >
                  + Add option
                </Typography>
              </Button>
            </Tooltip>
          </Box>
          <Divider
            sx={{
              background: theme.palette.Neutral[400],
              width: "252px",
              height: "1px",
            }}
          />
          <Box
            sx={{
              display: "flex",
              width: "100%",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "8px",
                alignSelf: "stretch",
              }}
            >
              <Typography variant="titleSmall">Hint (Optional)</Typography>
              <Typography variant="bodySmall">
                We will display this hint if the learner answers incorrectly
              </Typography>
            </Box>
            <TextField
              placeholder="Include question hint"
              multiline
              rows={5}
              defaultValue={hint}
              onChange={(e) => setHint(e.target.value)}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.Administrator.Dark.Default,
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
